import { useConnectionStatus, useStore, useTrash } from '@platewell/query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Photo } from '../components/photo.js';
import { quotaRefusal, UpgradeNotice, type QuotaRefusal } from '../components/upgrade-notice.js';

export function Trash() {
  const recipes = useTrash();
  const store = useStore();
  const { readOnly } = useConnectionStatus();
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<QuotaRefusal | null>(null);

  // Restoring a trashed recipe takes a slot back, so a full household is refused here the
  // same way a new recipe is. Restoring an already-live recipe never is.
  async function restore(recipeId: string): Promise<void> {
    setError(null);
    setRefusal(null);
    try {
      await store.restoreRecipe(recipeId);
    } catch (cause) {
      const quota = quotaRefusal(cause);
      if (quota != null) setRefusal(quota);
      else setError(cause instanceof Error ? cause.message : 'Could not restore the recipe');
    }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 18 }}>
        <h1>Trash</h1>
        <span className="spacer" />
        <span className="hint list-count">{recipes.length} recipes</span>
      </div>

      <p className="hint" style={{ marginBottom: 18 }}>
        Deleted recipes stay here so a delete on one device never surprises anyone on another.
      </p>

      {refusal != null && (
        <UpgradeNotice
          refusal={refusal}
          action={'restore this recipe \u2014 or delete one for good to free a slot'}
        />
      )}

      {error != null && (
        <p className="error-text" style={{ marginBottom: 18 }}>
          {error}
        </p>
      )}

      {recipes.length === 0 ? (
        <div className="empty">
          <h2>Nothing in the trash</h2>
        </div>
      ) : (
        <div className="grid">
          {recipes.map((recipe) => (
            <div className="card" key={recipe.id}>
              <Photo className="card-photo" blobId={recipe.photos[0]?.blobId ?? null} alt="" />
              <div className="card-body">
                <Link className="card-title" to="/recipes/$recipeId" params={{ recipeId: recipe.id }}>
                  {recipe.title}
                </Link>
                <span className="card-meta">
                  Deleted {new Date(recipe.deletedAt ?? 0).toLocaleDateString()}
                </span>
                {!readOnly && (
                  <button
                    className="btn"
                    style={{ marginTop: 8 }}
                    onClick={() => void restore(recipe.id)}
                  >
                    Restore
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
